// ============================================================
//  Bragsheet React — pwa.js
//  Install-prompt handling + service-worker registration. React port of
//  www/pwa.js's browser logic (the native-APK guard is irrelevant here — this
//  SPA is always a browser context).
//
//  - Captures `beforeinstallprompt` (Chrome/Edge/Android), stashing the event so
//    a real native install prompt can be fired from a user gesture.
//  - Tracks dismissals (max 2) in localStorage, and standalone/installed state.
//  - Exposes a tiny store (getState/subscribe/promptInstall/dismiss) the React
//    InstallBanner consumes, plus window.BragsheetPWA for parity.
//  - Registers the service worker in PRODUCTION ONLY (a SW caching Vite's dev
//    assets would fight HMR); this is what enables the reminder engine's
//    serviceWorker.controller delivery path.
// ============================================================

const MAX_DISMISSALS = 2;
const DISMISS_KEY = 'bragsheet_pwa_dismiss_count';

let deferredPrompt = null;
const listeners = new Set();

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true // iOS
  );
}
function getDismissCount() {
  try { return parseInt(localStorage.getItem(DISMISS_KEY), 10) || 0; } catch { return 0; }
}
function setDismissCount(n) {
  try { localStorage.setItem(DISMISS_KEY, String(n)); } catch { /* ignore */ }
}

function isIosSafari() {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
  const webkit = /WebKit/.test(ua);
  const notOtherBrowser = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iOS && webkit && notOtherBrowser;
}

// Honest, browser-specific manual steps (used when no programmatic prompt).
export function manualInstructions() {
  const ua = window.navigator.userAgent;
  if (isIosSafari()) return 'Tap the Share icon, then “Add to Home Screen”.';
  if (/Android/.test(ua)) return 'Open the browser menu (⋮), then “Install app” / “Add to Home screen”.';
  return 'Open the browser menu (⋮), then “Install Bragsheet” — or use the install icon in the address bar.';
}

function notify() { listeners.forEach((fn) => fn(getState())); }

export function getState() {
  return {
    canAutoPrompt: !!deferredPrompt,           // a real beforeinstallprompt is stashed
    standalone: isStandalone(),                // already installed
    dismissed: getDismissCount() >= MAX_DISMISSALS,
    ios: isIosSafari(),
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Fire the real native install prompt if available; returns true if shown. */
export function promptInstall() {
  if (isStandalone()) return false;
  if (!deferredPrompt) return false;
  deferredPrompt.prompt();
  deferredPrompt.userChoice.finally(() => {
    deferredPrompt = null;
    notify();
  });
  return true;
}

/** Record a banner dismissal (caps re-prompting at MAX_DISMISSALS). */
export function dismiss() {
  setDismissCount(getDismissCount() + 1);
  notify();
}

// Launched as an installed app ⇒ a prior install took: reset the count.
if (isStandalone()) setDismissCount(0);

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();          // suppress the mini-infobar; we drive the UI
  deferredPrompt = e;
  notify();
});
window.addEventListener('appinstalled', () => {
  setDismissCount(0);
  deferredPrompt = null;
  notify();
});

// Register the service worker in production only.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[pwa] service worker registration failed:', err);
    });
  });
}

// Parity with the vanilla global API.
if (typeof window !== 'undefined') {
  window.BragsheetPWA = { promptInstall, canAutoPrompt: () => !!deferredPrompt };
}
